import { useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Loader2, Copy, Coins, BookText, FileText, Paperclip, Users, Swords } from "lucide-react";
import { toast } from "sonner";

const OPTIONS = [
  { key: "costi", label: "Costi", icon: Coins, desc: "Voci di costo della pratica" },
  { key: "diario", label: "Diario e promemoria", icon: BookText, desc: "Movimenti del diario" },
  { key: "lettere", label: "Lettere generate", icon: FileText, desc: "Documenti salvati della pratica" },
  { key: "allegati", label: "Allegati (tutti)", icon: Paperclip, desc: "Tutti i file e cartelle (copiati fisicamente)" },
];

const subjLabel = (c) => c.ragione_sociale || [c.cognome, c.nome].filter(Boolean).join(" ").trim() || "";

export default function DuplicaPraticaDialog({ pratica, clienti = [], controparti = [], onClose, onDuplicated }) {
  const [sel, setSel] = useState({ costi: false, diario: false, lettere: false, allegati: false });
  const [selCli, setSelCli] = useState(() => new Set((clienti || []).map((_, i) => `cli-${i}`)));
  const [selCp, setSelCp] = useState(() => new Set((controparti || []).map((_, i) => `cp-${i}`)));
  const [saving, setSaving] = useState(false);
  const toggle = (k) => setSel((s) => ({ ...s, [k]: !s[k] }));
  const toggleSet = (setter) => (uid) => setter((s) => { const n = new Set(s); n.has(uid) ? n.delete(uid) : n.add(uid); return n; });
  const toggleCli = toggleSet(setSelCli);
  const toggleCp = toggleSet(setSelCp);

  const clientRows = (clienti || []).map((c, i) => ({
    uid: `cli-${i}`,
    key: c.cliente_id ? String(c.cliente_id) : "principale",
    label: subjLabel(c) || "Cliente",
    ruolo: c.ruolo || (i === 0 ? "Assistito principale" : "Assistito"),
  }));
  const cpRows = (controparti || []).map((c, i) => ({
    uid: `cp-${i}`,
    key: c.anagrafica_id || c.cliente_id || c.id || null,
    label: subjLabel(c) || "Controparte",
    ruolo: c.ruolo || "Controparte",
  }));

  const duplica = async () => {
    const selClients = (clienti || []).filter((_, i) => selCli.has(`cli-${i}`));
    if (selClients.length === 0) {
      toast.error("Seleziona almeno un cliente da includere nella nuova pratica.");
      return;
    }
    const selCparti = (controparti || []).filter((_, i) => selCp.has(`cp-${i}`));
    const nameOf = (c) => c.ragione_sociale || [c.cognome, c.nome].filter(Boolean).join(" ").trim() || "";
    const strip = (c) => { const { __principal, ...rest } = c || {}; return rest; };
    const first = selClients[0] || {};
    const firstComp = first.compagnia || first.assicurazione || "";
    const top = {
      cliente_id: first.cliente_id || null,
      nome_assistito: nameOf(first),
      assistito_ragione_sociale: nameOf(first),
      assistito_ruolo: first.ruolo || "",
      assistito_compagnia: firstComp,
      compagnia: firstComp,
      assistito_ispettorato: first.ispettorato || "",
      assistito_num_sinistro: first.num_sinistro || "",
      assistito_istruttore: first.istruttore || "",
      targa: first.targa || "",
      codice_fiscale: first.codice_fiscale || "",
      assistito_scheda: strip(first),
    };
    const assistitiNew = [
      { assicurazione: firstComp, compagnia: firstComp, ispettorato: first.ispettorato || "", targa: first.targa || "" },
      ...selClients.slice(1).map((c) => ({ ...strip(c), ragione_sociale: nameOf(c), cliente_id: c.cliente_id || null, ruolo: c.ruolo || "Assistito" })),
    ];
    setSaving(true);
    try {
      const payload = {
        ...sel,
        clienti_keys: clientRows.filter((r) => selCli.has(r.uid)).map((r) => r.key),
        controparti_keys: cpRows.filter((r) => selCp.has(r.uid) && r.key).map((r) => r.key),
        subjects: { top, assistiti: assistitiNew, controparti: selCparti },
      };
      const { data } = await api.post(`/studio/pratiche/${pratica.id}/duplica`, payload);
      toast.success(`Pratica duplicata: ${data.numero_pratica}`);
      onDuplicated && onDuplicated(data);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const SubjectSection = ({ title, icon: Icon, rows, selected, onToggle, testKey }) => (
    <div>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-400 font-mono mb-2">
        <Icon className="h-3.5 w-3.5" /> {title}
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-slate-500 px-1 pb-1">Nessuno presente in questa pratica.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <label key={r.uid} data-testid={`duplica-${testKey}-${r.uid}`}
              className={`flex items-center gap-3 rounded-md border px-3 py-2 cursor-pointer transition-colors ${selected.has(r.uid) ? "border-amber-500/50 bg-amber-500/10" : "border-slate-700 bg-slate-900/40 hover:bg-slate-800/50"}`}>
              <Checkbox checked={selected.has(r.uid)} onCheckedChange={() => onToggle(r.uid)} data-testid={`duplica-${testKey}-check-${r.uid}`} />
              <div className="min-w-0">
                <div className="text-sm text-slate-100 font-medium truncate">{r.label}</div>
                <div className="text-xs text-slate-500">{r.ruolo} · include i file allegati collegati</div>
              </div>
            </label>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="pgp-modal max-w-lg" data-testid="duplica-pratica-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="h-5 w-5 text-amber-400" /> Duplica Pratica
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 max-h-[62vh] overflow-y-auto pr-1">
          <div className="rounded-md border border-slate-700 bg-slate-900/40 px-3 py-2.5 text-sm">
            <p className="text-slate-300">Verrà creata una <b className="text-slate-100">nuova pratica</b> con un nuovo numero, stato <b className="text-slate-100">Aperta</b>. I dati generali (tipo, sinistro, veicoli, ecc.) vengono <b className="text-slate-100">sempre copiati</b>. Verranno inclusi <b className="text-slate-100">solo i clienti/controparti selezionati</b> qui sotto (di default tutti), con i rispettivi allegati.</p>
            <p className="text-slate-500 mt-1">Origine: <span className="font-mono text-amber-400">{pratica.numero_pratica}</span></p>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-400 font-mono mb-2">Copia anche (opzionale)</div>
            <div className="space-y-2">
              {OPTIONS.map((o) => (
                <label key={o.key} data-testid={`duplica-opt-${o.key}`}
                  className={`flex items-center gap-3 rounded-md border px-3 py-2.5 cursor-pointer transition-colors ${sel[o.key] ? "border-amber-500/50 bg-amber-500/10" : "border-slate-700 bg-slate-900/40 hover:bg-slate-800/50"}`}>
                  <Checkbox checked={sel[o.key]} onCheckedChange={() => toggle(o.key)} data-testid={`duplica-check-${o.key}`} />
                  <o.icon className="h-4 w-4 text-slate-400 shrink-0" />
                  <div>
                    <div className="text-sm text-slate-100 font-medium">{o.label}</div>
                    <div className="text-xs text-slate-500">{o.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <SubjectSection title="Clienti" icon={Users} rows={clientRows} selected={selCli} onToggle={toggleCli} testKey="cliente" />
          <SubjectSection title="Controparti" icon={Swords} rows={cpRows} selected={selCp} onToggle={toggleCp} testKey="controparte" />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" onClick={onClose} data-testid="duplica-annulla" className="border border-slate-700 bg-slate-800 text-white hover:bg-slate-700">Annulla</Button>
          <Button type="button" onClick={duplica} disabled={saving} data-testid="duplica-conferma" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Duplica pratica"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
